// src/pages/admin/AdminModerationPage.tsx
import React from 'react';
import { useModerationQueue, useModerationStats, useBulkApprove, useBulkReject } from '../hooks';
import { ModerationQueue } from '../components';
import { useAdminStore } from '@/features/admin/stores/adminStore';
import { useToast } from '@/core/hooks/useToast';

export const AdminModerationPage: React.FC = () => {
  const moderationFilters = useAdminStore((state) => state.moderationFilters);
  const setModerationFilters = useAdminStore((state) => state.setModerationFilters);
  const selectedItems = useAdminStore((state) => state.ui.selectedItems);
  const clearSelectedItems = useAdminStore((state) => state.clearSelectedItems);
  const { toast } = useToast();
  
  const { data: queue, isLoading } = useModerationQueue({
    item_type: moderationFilters.itemType,
    status: moderationFilters.status,
    priority: moderationFilters.priority,
  });

  const { data: stats } = useModerationStats();
  const bulkApproveMutation = useBulkApprove();
  const bulkRejectMutation = useBulkReject();

  const handleBulkApprove = () => {
    if (selectedItems.length === 0) return;
    bulkApproveMutation.mutate(selectedItems, {
      onSuccess: () => {
        toast({ title: 'Items approved successfully', variant: 'success' });
        clearSelectedItems();
      },
    });
  };

  const handleBulkReject = () => {
    if (selectedItems.length === 0) return;
    bulkRejectMutation.mutate({ ids: selectedItems, reason: 'Bulk rejection' }, {
      onSuccess: () => {
        toast({ title: 'Items rejected successfully', variant: 'success' });
        clearSelectedItems();
      },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold text-navy">Content Moderation</h1>
          <p className="text-warm-gray">Review and moderate user-generated content</p>
        </div>
        {selectedItems.length > 0 && (
          <div className="flex gap-2">
            <button
              onClick={handleBulkApprove}
              disabled={bulkApproveMutation.isPending}
              className="px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark"
            >
              Approve Selected ({selectedItems.length})
            </button>
            <button
              onClick={handleBulkReject}
              disabled={bulkRejectMutation.isPending}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
            >
              Reject Selected
            </button>
          </div>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-sm text-warm-gray">Pending</p>
            <p className="text-2xl font-bold text-navy">{stats.pending}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-sm text-warm-gray">Approved Today</p>
            <p className="text-2xl font-bold text-sage">{stats.approved_today}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-sm text-warm-gray">Rejected Today</p>
            <p className="text-2xl font-bold text-red-500">{stats.rejected_today}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-sm text-warm-gray">Avg Response Time</p>
            <p className="text-2xl font-bold text-navy">{stats.avg_response_time_hours}h</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4">
          <select
            value={moderationFilters.itemType || ''}
            onChange={(e) => setModerationFilters({ itemType: e.target.value || undefined })}
            className="px-4 py-2 border border-gray-200 rounded-lg"
          >
            <option value="">All Types</option>
            <option value="post">Posts</option>
            <option value="reply">Replies</option>
            <option value="review">Reviews</option>
            <option value="profile">Profiles</option>
          </select>
          <select
            value={moderationFilters.priority || ''}
            onChange={(e) => setModerationFilters({ priority: e.target.value || undefined })}
            className="px-4 py-2 border border-gray-200 rounded-lg"
          >
            <option value="">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>

      {/* Queue */}
      <ModerationQueue items={queue?.items || []} isLoading={isLoading} />
    </div>
  );
};

export default AdminModerationPage;




